// @givmo/board — the director's read of a meeting record, and the minutes waiting on their seat.
//
// ══ WHAT IS TYPED HERE, AND WHAT IS STILL NOT ══════════════════════════════════════════════════
//
// The two halves of this module are at different points, and conflating them is how the wrong one
// gets "tidied":
//
//   * **The meeting record IS typed.** `GET /board-director/…/meetings/{id}/record` returns
//     `BoardDirectorInMeetingRecordOut`, so every list below carries its generated element type and
//     nothing is hand-modelled. What survives is the RUNTIME tolerance: a generated type is a
//     compile-time claim about the wire, not a check of it, and this decode is the only thing between
//     a shape change and a blank screen for a volunteer holding an emailed link.
//   * **The attention list's ITEMS are typed now too.** They were `list[dict]`; a backend item has
//     since typed them, so `BoardDirectorAttentionOut.items` generates as a three-member union
//     discriminated on `kind` (meeting / consent / minutes). {@link minutesAwaitingDirector}
//     deliberately still takes `unknown` and reads field by field, for the same reason the record's
//     decode does: the generated union is a claim about the wire, not a check of it.
//
// ══ THE ABSENCE SEMANTICS, WHICH ARE THE POINT OF THIS FILE ════════════════════════════════════
//
// A closed session this director was not part of is **ABSENT** from `segments`, and every motion made
// inside it is absent from `motions`. The backend filters before projecting, and it projects **no
// ordinal**, so there is nothing on the wire from which a count of what was withheld could be
// recovered.
//
// So the decode returns **only what arrived**. There is deliberately:
//
//   * no `{ present: false }` variant, and no placeholder entry of any kind — the same rule
//     `records-decode.ts`'s `recordPointers()` follows, for the same reason: a caller must not be able
//     to render "something is here that you cannot see";
//   * no total, no expected count, and nothing compared against the meeting's own figures.
//
// ══ WHAT D-CBG-25 MADE PUBLIC, AND WHAT IT DID NOT (CBG-RC12-READ) ═════════════════════════════
//
// That a closed session HAPPENED is no longer withheld: the meeting's own minutes print its public
// entry, so `session_entries` carries every session in order — for a closed one its times, its
// general purpose, who stepped out for a conflict (names only) and the decisions the board didn't keep
// confidential (backend `board_minutes.md` §O6). Everything else a closed session holds — its title,
// who was in it, its other motions — stays in `segments` and `motions` for those it was sent to, and
// absent for everyone else, as above.
//
// Two facts on a closed entry are this director's own: `sent_to_you`, and `minutes_record_id` (its own
// minutes, when this director may open them). With the signing window shut both are null (§O12 C5):
// who was in a closed session is closed-session content, so the page asks for the confirmation before
// it shows either (`director-minutes-panel.tsx`).
//
// **An UNLINKED director sees standard sessions only**, fail-closed by construction on the backend:
// the scoping keys on the seat's LINKED account, and a seat need not have one. Being recorded as a
// segment participant does not change that — a closed-session record whose participants have no
// accounts is a record its own participants cannot read back, and closing that needs a backend change
// this app must not paper over with a client-side guess.

import type {
  BoardDirectorInMeetingRecordOut,
  BoardMeetingAttendanceOut,
  BoardMeetingSegmentOut,
  BoardMotionOut,
  BoardMotionRecusalOut,
  BoardMotionVoteOut,
  BoardSessionEntryOut,
} from '@givmo/api-client';

/**
 * One meeting's record as this seat may see it. Only ever what arrived.
 *
 * Every element type is the generated one. The shape differs from the wire's in exactly one way —
 * the two `my_*` lists are camelCased to match this app's read models — and it deliberately drops
 * `meeting_id`, which the caller already holds as a route parameter and which nothing here reads.
 */
export interface DirectorMeetingRecord {
  attendance: BoardMeetingAttendanceOut[];
  /** ⚠ Sessions this director may read. A withheld one is simply not here. */
  segments: BoardMeetingSegmentOut[];
  /** ⚠ Motions this director may read — those in withheld sessions are not here. */
  motions: BoardMotionOut[];
  /** This seat's own casts, so a director need not read every motion to find themselves. */
  myVotes: BoardMotionVoteOut[];
  myRecusals: BoardMotionRecusalOut[];
  /**
   * CBG-YR3-ROLES layer 2 (W-14, `l2-director-sign`): who was in the room and who wasn't, with each
   * person's capacity, as the ONE sentence the backend's minutes renderer writes ("Present: Ruth
   * Calloway (President), …"). The director has no roster to name the attendance rows by, so the
   * names reach them only this way. It covers the meeting's open attendance alone — nothing about a
   * closed session. `null` when none is recorded, or an answer from before it.
   */
  presenceStatement: string | null;
  /**
   * CBG-RC12-READ: EVERY session of the meeting, in order, as any reader of it may see it — a closed
   * one as its public entry (see the header). `null` from a backend that predates the field, which
   * then reads as before (`segments` alone).
   */
  sessionEntries: BoardSessionEntryOut[] | null;
}

/**
 * ⚠ Every list is checked at RUNTIME even though the response is typed.
 *
 * The generated type is a claim about the wire derived from a snapshot, not a validation of the bytes
 * that arrive — every list on `BoardDirectorInMeetingRecordOut` is optional, and a wire that answers
 * something else answers it regardless of what the type says. `Array.isArray` is what keeps a shape
 * change from throwing in a component a volunteer reached on an emailed link.
 */
function arrayOf<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

/**
 * Read the director's meeting record.
 *
 * Never throws and never invents. An unreadable or absent body yields five empty lists and no
 * presence sentence, which renders as a meeting with nothing recorded — the same thing a reader
 * entitled to nothing sees, deliberately.
 */
export function decodeDirectorMeetingRecord(
  record: BoardDirectorInMeetingRecordOut | null | undefined,
): DirectorMeetingRecord {
  return {
    attendance: arrayOf<BoardMeetingAttendanceOut>(record?.attendance),
    segments: arrayOf<BoardMeetingSegmentOut>(record?.segments),
    motions: arrayOf<BoardMotionOut>(record?.motions),
    myVotes: arrayOf<BoardMotionVoteOut>(record?.my_votes),
    myRecusals: arrayOf<BoardMotionRecusalOut>(record?.my_recusals),
    presenceStatement: sentence(record?.presence_statement),
    sessionEntries: Array.isArray(record?.session_entries)
      ? record.session_entries.filter(
          (entry): entry is BoardSessionEntryOut =>
            typeof entry === 'object' &&
            entry !== null &&
            typeof entry.id === 'string' &&
            entry.id.length > 0,
        )
      : null,
  };
}

/** Whether a session entry is a closed (executive) session's public entry. */
export function isClosedEntry(entry: BoardSessionEntryOut): boolean {
  return entry.segment_type === 'executive_session';
}

/**
 * CBG-RC12-READ: what this director may do about a closed session's own minutes, read off its public
 * entry (backend `board_minutes.md` §O6, §O12 C5):
 *
 *   * `ask` — the signing window is shut, so whether it was sent to them and its minutes' pointer
 *     were withheld (`sent_to_you` null): the page asks for the confirmation first, then reads again;
 *   * `open` — its own minutes may be opened by this director (`minutes_record_id`): sent to them, or
 *     opened as a current director, a step that is recorded;
 *   * `none` — nothing to open: no minutes stand yet, or this reader may not open them.
 */
export type ClosedMinutesWay =
  | { kind: 'ask' }
  | { kind: 'open'; recordId: string; sentToYou: boolean }
  | { kind: 'none' };

export function closedMinutesWay(entry: BoardSessionEntryOut): ClosedMinutesWay {
  if (entry.sent_to_you === null || entry.sent_to_you === undefined) return { kind: 'ask' };
  const recordId = typeof entry.minutes_record_id === 'string' ? entry.minutes_record_id : '';
  if (recordId.length === 0) return { kind: 'none' };
  return { kind: 'open', recordId, sentToYou: entry.sent_to_you === true };
}

/** Whether this director's own seat is among those who stepped out of a closed session. */
export function steppedOutIsMine(entry: BoardSessionEntryOut, seatId: string | null): boolean {
  if (seatId === null || seatId.length === 0) return false;
  return (entry.stepped_out ?? []).some((person) => person.board_member_id === seatId);
}

function sentence(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

/** A set of minutes waiting on this seat's signature. */
export interface MinutesAwaitingDirector {
  recordId: string;
  meetingId: string | null;
  title: string;
  /**
   * ⚠ The content address to echo when signing — **taken from this attention read**, which is the read
   * that told the director there was something to sign and named it. It is not re-fetched at the
   * moment of signing: a signature names the exact bytes a human was shown, and an echo obtained
   * separately would name bytes nobody looked at.
   *
   * `null` when the wire carried none, which withdraws the affordance rather than sending an empty
   * string the backend would refuse with a message about the minutes having changed.
   */
  contentHash: string | null;
  /**
   * CBG-YR3-ROLES layer 3: the title the board's adopted duties suggest this seat sign in — its
   * "Signs the minutes" title (backend `board-officer-roles.md` §14.5) — or `null` with no duties
   * adopted, or none of this seat's titles has that duty. "Signing as" starts on it; it is only a
   * suggestion, and a title the seat no longer holds is never sent (`chosenCapacity`).
   */
  suggestedCapacityTitleId: string | null;
}

/**
 * The `minutes_signature` entries of the attention list.
 *
 * The list obeys one rule — only what this director can ACT on — and the backend applies it: minutes
 * still in draft are not waiting on a signature, and minutes this seat has already signed are not
 * waiting on them even while another seat has yet to sign. Nothing here re-derives that; entries are
 * shown because the backend put them in the list.
 */
export function minutesAwaitingDirector(items: unknown): MinutesAwaitingDirector[] {
  if (!Array.isArray(items)) return [];
  const awaiting: MinutesAwaitingDirector[] = [];
  for (const raw of items) {
    if (typeof raw !== 'object' || raw === null) continue;
    const item = raw as Record<string, unknown>;
    if (item.kind !== 'minutes_signature') continue;
    const recordId = typeof item.reference_id === 'string' ? item.reference_id : null;
    if (recordId === null || recordId.length === 0) continue;
    const hash = typeof item.content_hash === 'string' ? item.content_hash.trim() : '';
    awaiting.push({
      recordId,
      meetingId: typeof item.meeting_id === 'string' ? item.meeting_id : null,
      title: typeof item.title === 'string' && item.title.length > 0 ? item.title : 'Minutes',
      contentHash: hash.length > 0 ? hash : null,
      suggestedCapacityTitleId:
        typeof item.suggested_capacity_title_id === 'string' &&
        item.suggested_capacity_title_id.length > 0
          ? item.suggested_capacity_title_id
          : null,
    });
  }
  return awaiting;
}
